import api from './api';
import {
    Psychology,
    Mic,
    Hearing,
    ModeEditOutline,
    MenuBook,
    TrendingUp,
    Star,
    Group,
    CheckCircle,
} from '@mui/icons-material';
import React from 'react';
import queryString from 'query-string';
import { IGetListAchievementsParams } from '@/features/student/StudentAchievements/interface';

class AchievementsService {
    async getAchievements(query: IGetListAchievementsParams): Promise<any> {
        const queryStr = queryString.stringify(query);
        const response = await api.get(`/achievements/?${queryStr}`);
        return response.data;
    }

    async getLastAchievement(): Promise<any> {
        const response = await api.get('/achievements/last/');
        return response.data;
    }
}

// Mock Data for Development
export const mockAchievements = [
    {
        id: '1',
        title: 'First Steps',
        description: 'Complete your first lesson and begin your learning journey',
        type: 'course',
        icon: React.createElement(Psychology),
        xp_reward: 50,
        required_value: 1,
        is_active: true,
    },
    {
        id: '2',
        title: 'Streak Master',
        description: 'Maintain a 7-day learning streak to show your dedication',
        type: 'streak',
        icon: React.createElement(TrendingUp),
        xp_reward: 100,
        required_value: 7,
        is_active: true,
    },
    {
        id: '3',
        title: 'Vocabulary Builder',
        description: 'Learn 50 new words to expand your vocabulary',
        type: 'exercise',
        icon: React.createElement(MenuBook),
        xp_reward: 75,
        required_value: 50,
        is_active: true,
    },
    {
        id: '4',
        title: 'Speaking Star',
        description: 'Complete 10 speaking exercises to improve your pronunciation',
        type: 'exercise',
        icon: React.createElement(Mic),
        xp_reward: 120,
        required_value: 10,
        is_active: true,
    },
    {
        id: '5',
        title: 'Grammar Guru',
        description: 'Master 20 grammar rules to become a language expert',
        type: 'exercise',
        icon: React.createElement(Psychology),
        xp_reward: 150,
        required_value: 20,
        is_active: true,
    },
    {
        id: '6',
        title: 'Listening Legend',
        description: 'Complete 15 listening exercises to enhance comprehension',
        type: 'exercise',
        icon: React.createElement(Hearing),
        xp_reward: 90,
        required_value: 15,
        is_active: true,
    },
    {
        id: '7',
        title: 'XP Pioneer',
        description: 'Earn your first 500 XP points',
        type: 'xp',
        icon: React.createElement(Star),
        xp_reward: 200,
        required_value: 500,
        is_active: true,
    },
    {
        id: '8',
        title: 'Week Warrior',
        description: 'Complete lessons for 5 consecutive days',
        type: 'streak',
        icon: React.createElement(TrendingUp),
        xp_reward: 80,
        required_value: 5,
        is_active: true,
    },
    {
        id: '9',
        title: 'Perfect Score',
        description: 'Get 100% on any assessment',
        type: 'special',
        icon: React.createElement(CheckCircle),
        xp_reward: 300,
        required_value: 1,
        is_active: true,
    },
    {
        id: '10',
        title: 'Social Butterfly',
        description: 'Participate in 5 group discussions',
        type: 'special',
        icon: React.createElement(Group),
        xp_reward: 60,
        required_value: 5,
        is_active: true,
    },
    {
        id: '11',
        title: 'Reading Champion',
        description: 'Complete 25 reading exercises',
        type: 'exercise',
        icon: React.createElement(MenuBook),
        xp_reward: 110,
        required_value: 25,
        is_active: true,
    },
    {
        id: '12',
        title: 'Writing Wizard',
        description: 'Submit 15 writing assignments',
        type: 'exercise',
        icon: React.createElement(ModeEditOutline),
        xp_reward: 130,
        required_value: 15,
        is_active: true,
    },
];

export const mockUserAchievements = [
    {
        id: '1',
        user: 'user1',
        achievement: mockAchievements[0], // First Steps
        earned_at: '2024-01-15T10:30:00Z',
        progress: 1,
        completed: true,
    },
    {
        id: '2',
        user: 'user1',
        achievement: mockAchievements[1], // Streak Master
        earned_at: '2024-01-20T14:15:00Z',
        progress: 7,
        completed: true,
    },
    {
        id: '3',
        user: 'user1',
        achievement: mockAchievements[2], // Vocabulary Builder
        earned_at: null,
        progress: 35,
        completed: false,
    },
    {
        id: '4',
        user: 'user1',
        achievement: mockAchievements[3], // Speaking Star
        earned_at: null,
        progress: 7,
        completed: false,
    },
    {
        id: '5',
        user: 'user1',
        achievement: mockAchievements[4], // Grammar Guru
        earned_at: null,
        progress: 12,
        completed: false,
    },
    {
        id: '6',
        user: 'user1',
        achievement: mockAchievements[5], // Listening Legend
        earned_at: null,
        progress: 0,
        completed: false,
    },
    {
        id: '7',
        user: 'user1',
        achievement: mockAchievements[6], // XP Pioneer
        earned_at: null,
        progress: 320,
        completed: false,
    },
    {
        id: '8',
        user: 'user1',
        achievement: mockAchievements[7], // Week Warrior
        earned_at: null,
        progress: 3,
        completed: false,
    },
    {
        id: '9',
        user: 'user1',
        achievement: mockAchievements[8], // Perfect Score
        earned_at: null,
        progress: 0,
        completed: false,
    },
    {
        id: '10',
        user: 'user1',
        achievement: mockAchievements[9], // Social Butterfly
        earned_at: null,
        progress: 2,
        completed: false,
    },
    {
        id: '11',
        user: 'user1',
        achievement: mockAchievements[10], // Reading Champion
        earned_at: null,
        progress: 18,
        completed: false,
    },
    {
        id: '12',
        user: 'user1',
        achievement: mockAchievements[11], // Writing Wizard
        earned_at: null,
        progress: 8,
        completed: false,
    },
];

export const mockAchievementStats = {
    totalAchievements: 12,
    completedAchievements: 2,
    totalXpEarned: 150,
    completionRate: 16.67,
    recentAchievements: mockUserAchievements.filter((ua) => ua.completed).slice(0, 3),
    nextAchievements: mockAchievements
        .filter((a) => !mockUserAchievements.find((ua) => ua.achievement.id === a.id && ua.completed))
        .slice(0, 5),
};

export default new AchievementsService();
